import { Button, Form, Frequency, Stack, Text, useBasisForm } from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { getRegional } from "library/getRegional";
import { getNumberOfSteps } from "pages/HardshipApplication/reducer/hardshipSelectors";
import { usePageTracking } from "react-event-tracker";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import { ActionTypes, YourIncomeState } from "../reducer/types";

export const YourIncome = () => {
  const { state, dispatch } = useHardshipApplicationContext();
  const { yourIncome } = state;

  const { methods, Field } = useBasisForm<YourIncomeState>({
    defaultValues: yourIncome,
  });
  const { navigate, goBack } = useNav();

  const onSubmit = (values: YourIncomeState) => {
    dispatch({ type: ActionTypes.COMPLETE_YOUR_INCOME, ...values });
    navigate("expenses");
  };

  const isNZ = getRegional("code") === "NZ";

  const numberOfSteps = getNumberOfSteps(state);

  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "hardship",
      siteSubSection: "income",
      pageTitle: "What do you earn?",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8">
            <Stack gap="4">
              <Text>
                <strong>Step 5 of {numberOfSteps}</strong>
              </Text>
              <Text as="h2" textStyle="heading4">
                What do you earn?
              </Text>
              <Text>
                To help us get a clear picture of your financial situation, we
                need you to fill in every income field here.{" "}
                <strong>
                  So even if it doesn’t apply to you, please enter ‘0’.
                </strong>
              </Text>
            </Stack>
            <Stack gap="8" width="100%" width-sm="400">
              <Field
                label="Salary (after tax)"
                name="salary"
                amountPrefix="$"
                mode="select"
                as={Frequency}
              />
              <Field
                label="Wages (after tax)"
                name="wages"
                amountPrefix="$"
                mode="select"
                as={Frequency}
              />
              {!isNZ && (
                <Field
                  label="Centrelink"
                  name="centrelink"
                  amountPrefix="$"
                  mode="select"
                  as={Frequency}
                />
              )}
              <Field
                label="Business income"
                name="businessIncome"
                amountPrefix="$"
                mode="select"
                as={Frequency}
              />
              {isNZ && (
                <Stack gap="8">
                  <Field
                    label="Rent/Board income"
                    name="rentBoard"
                    amountPrefix="$"
                    mode="select"
                    as={Frequency}
                  />
                  <Field
                    label="Investment income"
                    name="investment"
                    amountPrefix="$"
                    mode="select"
                    as={Frequency}
                  />
                  <Field
                    label="WINZ Benefits"
                    name="winzBenefit"
                    amountPrefix="$"
                    mode="select"
                    as={Frequency}
                  />
                  <Field
                    label="IRD Family Tax Credit"
                    name="irdFamilyTaxCredit"
                    amountPrefix="$"
                    mode="select"
                    as={Frequency}
                  />
                  <Field
                    label="IRD Child Support"
                    name="irdChildSupport"
                    amountPrefix="$"
                    mode="select"
                    as={Frequency}
                  />
                </Stack>
              )}
              <Field
                label="Other"
                name="other"
                amountPrefix="$"
                mode="select"
                as={Frequency}
              />
              <FormActions>
                <FormActions.Primary>
                  <Button variant="primary" type="submit">
                    Next
                  </Button>
                </FormActions.Primary>
                <FormActions.Secondary>
                  <Button variant="secondary" onClick={goBack}>
                    Back
                  </Button>
                </FormActions.Secondary>
              </FormActions>
            </Stack>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
